/**
 * Pure result mappers: Slice 5 workloads `ValidationReport` / `RunView` →
 * the existing local `WorkPatternImportResult` shape consumed by
 * `useWorkPatternImportInvocation`, `workPatternImportClustering.ts`, and the
 * report/table components.
 *
 * LADR-039 (#440) Step 7 — see `.plans/ladr-039-contract-mapping.md`'s "work
 * patterns" vertical table for the authoritative field mapping this module
 * implements, and its "Summary of residual mismatches" #1 for the
 * `workPatternName` re-derivation this module performs. No React/hooks here;
 * safe to unit test directly and to reuse from the Step 8 workloads-backed
 * manager without pulling in TanStack Query.
 */
import {
  MigrationRunStatus,
  type RowValidationError,
  type RunView,
  type ValidationReport,
  type WorkPatternRow,
} from "../../../generated/core-api";
import type {
  WorkPatternImportApiWarning,
  WorkPatternImportError,
  WorkPatternImportResult,
} from "./useWorkPatternImportInvocation";

/**
 * `RowValidationError.row_index` has no echoed-back `workPatternName` (unlike
 * Track 1's `errors[].workPatternName`) — re-derive it by joining against the
 * last-known canonical rows (the same array the report/table already render
 * against). Falls back to a `Row N` label if the index is out of range,
 * which should not happen in practice but keeps the mapper total.
 */
function workPatternNameForRow(
  canonicalRows: WorkPatternRow[],
  rowIndex: number
): string {
  return canonicalRows[rowIndex]?.workPatternName ?? `Row ${rowIndex + 1}`;
}

/**
 * Groups Track 2's one-`RowValidationError`-per-(row, column) shape into
 * Track 1's one-`WorkPatternImportError`-per-row shape, joining multiple
 * column messages for the same row with `"; "` — the same join convention
 * `useWorkPatternImportInvocation`/`workPatternImportClustering.ts` already
 * use for merging multiple messages onto one row. This also preserves
 * `workPatternImportClustering.ts`'s `errorByIndex` `Map` invariant (at most
 * one error entry per pattern index) instead of silently dropping all but
 * the last column error for a row.
 */
export function mapRowValidationErrorsToWorkPatternErrors(
  errors: RowValidationError[] | undefined,
  canonicalRows: WorkPatternRow[]
): WorkPatternImportError[] {
  const messagesByRow = new Map<number, string[]>();
  for (const error of errors ?? []) {
    const bucket = messagesByRow.get(error.row_index);
    if (bucket) {
      bucket.push(error.message);
    } else {
      messagesByRow.set(error.row_index, [error.message]);
    }
  }
  return Array.from(messagesByRow.entries())
    .sort(([a], [b]) => a - b)
    .map(([index, messages]) => ({
      index,
      workPatternName: workPatternNameForRow(canonicalRows, index),
      error: messages.join("; "),
    }));
}

/**
 * Unlike errors, Track 1's `WorkPatternImportApiWarning[]` was never grouped
 * one-per-row at this layer — `mergeWarningsByIndex` (in
 * `useWorkPatternImportInvocation`) already collapses multiple same-row
 * messages downstream — so this maps `RowValidationError[]` 1:1.
 */
export function mapRowValidationWarningsToWorkPatternWarnings(
  warnings: RowValidationError[] | undefined,
  canonicalRows: WorkPatternRow[]
): WorkPatternImportApiWarning[] {
  return (warnings ?? []).map((warning) => ({
    index: warning.row_index,
    workPatternName: workPatternNameForRow(canonicalRows, warning.row_index),
    warning: warning.message,
  }));
}

/**
 * Maps the validate-phase `ValidationReport` (from `useOnboardingValidation`'s
 * polled `ValidationView.report`) to the local `WorkPatternImportResult`
 * shape — the Track 2 replacement for `useWorkPatternImportInvocation`'s
 * `validationReport`.
 *
 * `report` is nullable to cover `ValidationView.report` being `null`/absent
 * while the validation job is still queued/running; callers (Step 8) are
 * expected to only treat the result as final once the job status is
 * terminal, mirroring Track 1's `validationReport` staying `null` until the
 * workflow poll reaches `COMPLETED`.
 */
export function mapValidationReportToWorkPatternImportResult(
  report: ValidationReport | null | undefined,
  canonicalRows: WorkPatternRow[],
  parseFailCount = 0
): WorkPatternImportResult {
  const errors = mapRowValidationErrorsToWorkPatternErrors(
    report?.errors,
    canonicalRows
  );
  const warnings = mapRowValidationWarningsToWorkPatternWarnings(
    report?.warnings,
    canonicalRows
  );
  const total = canonicalRows.length + parseFailCount;
  const failed = errors.length + parseFailCount;
  return {
    total,
    succeeded: total - failed,
    failed,
    errors,
    warnings,
  };
}

/**
 * Maps the commit-phase `RunView` (from `useOnboardingMigrationRun`'s polled
 * result) plus the last-known pre-run `ValidationReport` to the local
 * `WorkPatternImportResult` shape — the Track 2 replacement for
 * `useWorkPatternImportInvocation`'s `commitResult`.
 *
 * Residual mismatch (contract note, Step 7): unlike Track 1's per-row commit
 * result (`errors[]` keyed by row), `RunView` carries **no per-row outcome**
 * — only `pushed_versions` (which entity types/etags were pushed) and an
 * overall `status`. By the time a run is `POST`ed the canonical rows are
 * assumed already-validated (the manager only calls this after a clean
 * validate pass), so:
 * - `status: "succeeded"` → every canonical row is reported succeeded (there
 *   is nothing more granular to report).
 * - any other status (`"failed"`, or a non-terminal status passed in error)
 *   → returns `null`. A whole-run failure is a coarser signal than Track 1's
 *   per-row `errors[]` and has no row to attach itself to; callers must
 *   surface it as a manager-level error instead of a structured commit
 *   result, mirroring Track 1's `commitResult` staying `null` on a
 *   non-`COMPLETED` terminal poll status.
 *
 * `lastValidationReport`'s `warnings` (Christmas dry-run advisories) are
 * carried forward on success for parity with Track 1's `commitResult`, which
 * always included a `warnings` array (`readApiWarnings` returning `[]` when
 * absent).
 */
export function mapRunViewToWorkPatternImportResult(
  run: RunView,
  canonicalRows: WorkPatternRow[],
  lastValidationReport?: ValidationReport | null
): WorkPatternImportResult | null {
  if (run.status !== MigrationRunStatus.succeeded) {
    return null;
  }
  const total = canonicalRows.length;
  return {
    total,
    succeeded: total,
    failed: 0,
    errors: [],
    warnings: mapRowValidationWarningsToWorkPatternWarnings(
      lastValidationReport?.warnings,
      canonicalRows
    ),
  };
}
